import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import humanFormat from 'human-format';
import Fact from './fact';
import GrowthSinceInceptionFact from './growth-since-inception-fact';
import Fee from './fee';
import Code from './code';
import NoPerformanceFee from './no-performance-fee';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 20px;

  > div {
    padding-right: 20px;

    &:last-child {
      width: fit-content;
      padding-right: 0;
    }
  }

  ${props => props.theme.isLarge && `
    justify-content: flex-start;

    > div {
      padding-right: 48px;
    }
  `};

  ${props => props.theme.isMobile && `
    margin-top: 0px;

    & > div:nth-child(2) {
      width: calc(100% - 200px);
    }
  `};

  ${props => !props.theme.isFeaturedMedium && `
    width: auto;
  `};
`;

// eslint-disable-next-line complexity
export default function Facts({
  code, codeType, fundType, investmentHorizon, minimumInvestment, inceptionDate,
  growth, currency, managementFee, performanceFee, financialYearEndFeesDate,
  isPerformanceFeeChargedYes, isPerformanceFeeChargedNo,
}) {
  const CODE_TYPE_TO_LABEL = {
    apir: 'apir',
    stock: null,
  };

  const money = (minimumInvestment > 0 && currency)
    ? _.toUpper(`$${humanFormat(minimumInvestment, { decimals: 0, separator: '' })}`)
    : null;
  const showMin = minimumInvestment > 0 && !!currency;
  const showCode = fundType !== 'ETF' || codeType === 'stock';
  const showInception = !!inceptionDate && !Number.isNaN(growth);
  const { isXLarge } = React.useContext(ThemeContext);
  const performanceFeeComponent = isPerformanceFeeChargedNo
    ? <NoPerformanceFee />
    : <Fee label="actual performance fee" value={performanceFee} date={financialYearEndFeesDate} />;

  return (
    <Container>
      {!!investmentHorizon && <Fact label="investment horizon" value={investmentHorizon} />}
      {!!showMin && <Fact label="minimum investment" value={money} />}
      {!!showInception && <GrowthSinceInceptionFact date={inceptionDate} percent={growth} />}
      {!!managementFee && (
        <Fee
          label="management fee"
          value={managementFee}
          isPerformanceFeeChargedYes={isPerformanceFeeChargedYes}
          isPerformanceFeeChargedNo={isPerformanceFeeChargedNo}
        />
      )}
      {isXLarge && performanceFeeComponent}
      {!!showCode && (
        <Code
          codeLabel={CODE_TYPE_TO_LABEL[codeType]}
          code={code}
        />
      )}
    </Container>
  );
}
// eslint-enable-next-line complexity

Facts.propTypes = {
  code: PropTypes.string.isRequired,
  codeType: PropTypes.oneOf(['apir', 'stock']).isRequired,
  fundType: PropTypes.string,
  investmentHorizon: PropTypes.string,
  minimumInvestment: PropTypes.number,
  inceptionDate: PropTypes.string,
  growth: PropTypes.number,
  currency: PropTypes.string,
  managementFee: PropTypes.string,
  performanceFee: PropTypes.string,
  financialYearEndFeesDate: PropTypes.string,
  isPerformanceFeeChargedYes: PropTypes.bool.isRequired,
  isPerformanceFeeChargedNo: PropTypes.bool.isRequired,
};

Facts.defaultProps = {
  fundType: null,
  investmentHorizon: null,
  minimumInvestment: 0,
  inceptionDate: null,
  growth: Number.NaN,
  currency: null,
  managementFee: null,
  performanceFee: null,
  financialYearEndFeesDate: null,
};
